import {
  useMemo,
  useState,
  useCallback,
  useRef,
  FC,
  PropsWithChildren,
  useEffect,
} from "react";
import { InvoiceModel, useRestApiProvider } from "@jugl-web/rest-api";
import { useEffectOnce, usePrevious } from "react-use";
import { useToast, useTranslations } from "@jugl-web/utils";
import {
  InvoicesTableContext,
  InvoicesTableContextType,
} from "./InvoicesTableContext";
import { useSelectedItems } from "../hooks/useSelectedItems";
import { useAvailableActions } from "../hooks/useAvailableActions";
import {
  ComponentProps,
  CustomerComponentProps,
  TaskComponentProps,
} from "../types";

export const InvoicesTableProvider: FC<PropsWithChildren<ComponentProps>> = ({
  children,
  entity,
  config,
  isGuest = false,
  searchQuery,
  filters,
  variant,
  actions,
  onNavigate,
  ...props
}) => {
  const taskId =
    variant === "task" ? (props as TaskComponentProps).taskId : undefined;
  const customerId =
    variant === "customer"
      ? (props as CustomerComponentProps).customerId
      : undefined;

  const { t } = useTranslations();
  const { invoicesApi } = useRestApiProvider();
  const [isFetchingInternal, setIsFetchingInternal] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [items, setItems] = useState<InvoiceModel[]>([]);
  const [allLoadedItems, setAllLoadedItems] = useState<InvoiceModel[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalEntries, setTotalEntries] = useState(0);
  const { toast } = useToast({ variant: "web" });
  const availableActions = useAvailableActions({ variant, isGuest });

  const currentSearchQueryRef = useRef<string | undefined>(searchQuery);

  useEffect(() => {
    currentSearchQueryRef.current = searchQuery;
  }, [searchQuery]);

  const getInvoicesListLazy = isGuest
    ? invoicesApi.useLazyGuestInvoicesListQuery
    : invoicesApi.useLazyInvoicesListQuery;

  const [loadMoreItems, { isLoading }] = getInvoicesListLazy({});

  const [deleteInvoice] = invoicesApi.useDeleteInvoiceMutation();
  const [bulkDeleteInvoices] = invoicesApi.useBulkDeleteInvoicesMutation();

  const loadMoreInvoices = useCallback(
    async (shouldReset?: boolean) => {
      if (
        !shouldReset &&
        (isLoading || !hasMore || isFetchingInternal || !isInitialized)
      ) {
        return;
      }

      if (shouldReset) {
        setIsInitialLoading(true);
      }
      setIsFetchingInternal(true);
      if (shouldReset) {
        setPage(1);
        setItems([]);
        setHasMore(true);
      }

      const requestSearchQuery = currentSearchQueryRef.current;

      // For guest requests, taskId is required
      if (isGuest && !taskId) {
        setIsFetchingInternal(false);
        if (shouldReset) {
          setIsInitialLoading(false);
        }
        throw new Error("Task ID is required for guest requests");
      }

      const response = await loadMoreItems({
        params: {
          page: shouldReset ? 1 : page + 1,
          page_size: 20,
          search: requestSearchQuery || undefined,
          task_id: taskId || undefined,
          customer: customerId || undefined,
          ...(filters || {}),
        },
        isGuest,
        entityId: entity.id,
      });

      if (
        requestSearchQuery === currentSearchQueryRef.current &&
        response?.data?.data
      ) {
        const { data } = response.data;
        if (response.data.page_number === response.data.total_pages) {
          setHasMore(false);
        }
        setItems((prev) => {
          const uniqueItems = new Map(
            [...prev, ...data].map((item) => [item.id, item])
          );
          return Array.from(uniqueItems.values());
        });

        // Always accumulate all loaded items regardless of search
        setAllLoadedItems((prevAll) => {
          const uniqueAllItems = new Map(
            [...prevAll, ...data].map((item) => [item.id, item])
          );
          return Array.from(uniqueAllItems.values());
        });

        setTotalEntries(response.data.total_entries);
        setPage(response.data.page_number);
      }

      setIsFetchingInternal(false);
      if (shouldReset) {
        setIsInitialLoading(false);
      }
    },
    [
      isLoading,
      hasMore,
      isFetchingInternal,
      isInitialized,
      loadMoreItems,
      page,
      taskId,
      customerId,
      filters,
      entity.id,
      isGuest,
    ]
  );

  const handleDeleteInvoice = useCallback(
    async (invoiceIds: string | string[]) => {
      try {
        if (Array.isArray(invoiceIds)) {
          await bulkDeleteInvoices({
            entityId: entity.id,
            ids: invoiceIds,
          }).unwrap();

          setItems((prevItems) =>
            prevItems.filter((item) => !invoiceIds.includes(item.id))
          );
          setAllLoadedItems((prevItems) =>
            prevItems.filter((item) => !invoiceIds.includes(item.id))
          );
          setTotalEntries((prev) => prev - invoiceIds.length);
          toast(
            t({
              id: "invoices.table.deleted.success",
              defaultMessage: "Documents deleted successfully",
            }),
            { variant: "success" }
          );
        } else {
          await deleteInvoice({
            entityId: entity.id,
            invoiceId: invoiceIds,
          }).unwrap();
          setItems((prevItems) =>
            prevItems.filter((item) => item.id !== invoiceIds)
          );
          setAllLoadedItems((prevItems) =>
            prevItems.filter((item) => item.id !== invoiceIds)
          );
          setTotalEntries((prev) => prev - 1);
          toast("Invoice deleted successfully", { variant: "success" });
        }
      } catch (error) {
        toast("Failed to delete invoice(s)", { variant: "error" });
      }
    },
    [bulkDeleteInvoices, entity.id, toast, t, deleteInvoice]
  );

  const isInitializedRef = useRef(false);
  useEffectOnce(() => {
    setIsFetchingInternal(true);
    setIsInitialLoading(true);
    const loadItems = async () => {
      const requestSearchQuery = currentSearchQueryRef.current;

      if (isGuest && !taskId) {
        toast(
          t({
            id: "invoices.table.task-id-required",
            defaultMessage: "Task ID is required for guest requests",
          }),
          { variant: "error" }
        );
        setIsInitialized(true);
        setIsFetchingInternal(false);
        setIsInitialLoading(false);
        return;
      }

      const response = await loadMoreItems({
        params: {
          page: 1,
          page_size: 20,
          search: requestSearchQuery || undefined,
          task_id: taskId || undefined,
          customer: customerId || undefined,
          ...(filters || {}),
        },
        entityId: entity.id,
        isGuest,
      });

      if (
        requestSearchQuery === currentSearchQueryRef.current &&
        response?.data?.data
      ) {
        setTotalEntries(response.data.total_entries);
        setItems(response.data?.data);
        setAllLoadedItems(response.data?.data);
        if (response.data.total_pages === 1) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      }
      setIsInitialized(true);
      setIsFetchingInternal(false);
      setIsInitialLoading(false);
    };
    isInitializedRef.current = true;
    loadItems();
  });

  const previousSearchQuery = usePrevious(searchQuery);
  const previousFilters = usePrevious(filters);
  useEffect(() => {
    const hasSearchQueryChanged = previousSearchQuery !== searchQuery;
    const hasFiltersChanged = previousFilters !== filters;
    if (
      !isInitializedRef.current ||
      (!hasSearchQueryChanged && !hasFiltersChanged)
    ) {
      return;
    }
    loadMoreInvoices(true);
  }, [
    searchQuery,
    previousSearchQuery,
    loadMoreInvoices,
    filters,
    previousFilters,
  ]);

  const selectedItems = useSelectedItems(items, allLoadedItems);

  const addItem = useCallback(
    (item: InvoiceModel) => {
      if (filters?.type && !filters.type.includes(item.type)) {
        return;
      }
      setItems((prev) => [item, ...prev]);
      setTotalEntries((prev) => prev + 1);
      setAllLoadedItems((prev) => [item, ...prev]);
    },
    [filters]
  );

  const value: InvoicesTableContextType = useMemo(
    () => ({
      variant,
      items,
      selected: selectedItems,
      isLoading: isFetchingInternal,
      isInitialLoading,
      hasMore,
      totalEntries,
      isInitialized,
      isGuest,
      searchQuery,
      taskId,
      filters,
      entity,
      config,
      availableActions,
      loadMoreInvoices,
      handleDeleteInvoice,
      addItem,
      actions,
      onNavigate,
    }),
    [
      variant,
      items,
      selectedItems,
      isFetchingInternal,
      isInitialLoading,
      hasMore,
      totalEntries,
      isInitialized,
      isGuest,
      searchQuery,
      taskId,
      filters,
      entity,
      config,
      availableActions,
      loadMoreInvoices,
      handleDeleteInvoice,
      addItem,
      actions,
      onNavigate,
    ]
  );

  return (
    <InvoicesTableContext.Provider value={value}>
      {children}
    </InvoicesTableContext.Provider>
  );
};
